/**
 * Browser twin of the production reflective-dialogue guard.
 * Keep classifications aligned with server/services/reflectiveDialogue.js.
 */
export type MentorReality = {
  id: string;
  gravity: 'PROFOUND' | 'ELEVATED' | 'STEADY';
  reframe: string;
  hold: string;
  invitation: string;
  validation: string;
  summary: [string, string];
};

const REALITIES: Array<{ test: RegExp; value: MentorReality }> = [
  {
    test: /\b(died|dying|passed away|passed on|funeral|grief|grieving|mourning|widow|widower|lost (my|her|his|our)|buried)\b/i,
    value: {
      id: 'bereavement',
      gravity: 'PROFOUND',
      reframe:
        'Sitting with a loss that deep — while ordinary life keeps asking you to carry on — is an immense weight.',
      hold:
        'There is no tidy way to hold something like that, and nothing here needs to be made to sound manageable.',
      invitation:
        'If you want to say more about how these days have actually been, there is room for it here.',
      validation:
        'What has entered this conversation is grief, and it deserves more room than a quick business question.',
      summary: [
        'A major loss has changed the support structure underneath daily life.',
        'The hardest pressure is arriving in ordinary moments, where it can be carried unseen.',
      ],
    },
  },
  {
    test: /\b(alone|lonely|loneliness|no one|nobody|by myself|isolated|empty house|quiet house|no ?one to talk|estranged)\b/i,
    value: {
      id: 'isolation',
      gravity: 'PROFOUND',
      reframe:
        'When reliable human contact falls away, the quiet can become a burden rather than a place to recover.',
      hold:
        'That is a real strain to carry, even when nothing outwardly dramatic is happening.',
      invitation:
        'If you would like to say more about how that has been landing on you, there is room for it here.',
      validation:
        'The support that once absorbed some of the pressure is no longer holding the same shape.',
      summary: [
        'The personal support baseline has thinned while the demands have continued.',
        'Long stretches without meaningful contact are reducing the reserves available for work and life.',
      ],
    },
  },
  {
    test: /\b(can'?t cope|falling apart|breaking down|cannot go on|can'?t go on|everything is too much|completely overwhelmed)\b/i,
    value: {
      id: 'severe_overwhelm',
      gravity: 'PROFOUND',
      reframe:
        'The load has moved beyond ordinary pressure and is taking more from you than you have left to give.',
      hold:
        'That deserves a pause, not another demand to solve or explain it quickly.',
      invitation: 'We can slow this right down; share only what feels possible from here.',
      validation:
        'The pressure has reached a point where endurance alone is no longer an honest measure of how things are going.',
      summary: [
        'The current load is exceeding the capacity available to absorb it.',
        'Immediate steadiness matters more than adding another performance demand.',
      ],
    },
  },
  {
    test: /\b(payroll|cash flow|cashflow|runway|insolvent|bankrupt|debt|overdue|creditors?|rent|mortgage|broke|financial)\b/i,
    value: {
      id: 'cash_pressure',
      gravity: 'ELEVATED',
      reframe:
        'Financial pressure is narrowing the room to think, because every decision now carries consequences for more than the numbers.',
      hold:
        'That kind of responsibility keeps running in the background, even when the working day ends.',
      invitation:
        'Which pressure is setting the pace right now: immediate cash, ongoing margin, or the commitments attached to both?',
      validation:
        'The financial strain is shaping both the decisions and the amount of calm available to make them.',
      summary: [
        'Short-term cash pressure is squeezing how long you have to decide.',
        'What you do next needs a clearer split between bills that cannot wait and guesses you can still test.',
      ],
    },
  },
  {
    test: /\b(overwork|overtime|workload|deadline|deadlines|too much work|exhausted|drained|burnt out|burned out|no time|weekends?)\b/i,
    value: {
      id: 'workload',
      gravity: 'ELEVATED',
      reframe:
        'The pace of the work is consuming the capacity needed to step back and make sound decisions about it.',
      hold:
        'Running that way for a sustained period has a real cost, even when delivery is still happening.',
      invitation:
        'Where is the pressure multiplying fastest: volume, unclear priorities, or too much resting with one person?',
      validation:
        'The issue is no longer only the amount of work; it is the shrinking space available to lead it well.',
      summary: [
        'Delivery pressure is absorbing the time needed for oversight and correction.',
        'The workload is being sustained by personal reserves rather than a repeatable operating rhythm.',
      ],
    },
  },
  {
    test: /\b(staff|team|employee|employees|hire|hiring|manager|leadership|culture|conflict|people)\b/i,
    value: {
      id: 'people',
      gravity: 'STEADY',
      reframe:
        'The people issue is exposing a gap between what the business needs from the team and what the current operating environment enables.',
      hold:
        'That is rarely solved by assigning blame; it becomes clearer when expectations, capacity, and leadership signals are separated.',
      invitation:
        'Which part is least reliable at present: clarity, capability, or follow-through?',
      validation:
        'The team dynamic is pointing to a structural issue rather than a single difficult person.',
      summary: [
        'Expectations and available capability are not fully aligned.',
        'Leadership signals may be carrying more ambiguity than the current pace can tolerate.',
      ],
    },
  },
  {
    test: /\b(price|pricing|margin|discount|discounts|cost|costs|profit|profitable|revenue|membership|monthly|annual|retainer|retainers)\b/i,
    value: {
      id: 'pricing',
      gravity: 'STEADY',
      reframe:
        'Price is never only a number. It is a bet on whether people will stay once the first payment is easy.',
      hold:
        'Monthly terms can open the door and still leave you carrying the cash risk.',
      invitation:
        'What would actually tell you they are good for the work, not only good for getting in cheap?',
      validation:
        'The fear is not the annual figure. It is being stuck with people who stall after month one.',
      summary: [
        'Easy entry and reliable payment are two different problems.',
        'The test is who stays, not who signs.',
      ],
    },
  },
  {
    test: /\b(customer|customers|client|clients|sales|selling|demand|leads|churn|retention)\b/i,
    value: {
      id: 'customer_demand',
      gravity: 'STEADY',
      reframe:
        'The demand question is less about activity and more about whether the market is giving reliable evidence of value.',
      hold:
        'Busy pipelines can hide weak conversion or retention, so the useful signal needs to be made explicit.',
      invitation:
        'Where does confidence fall away first: attracting interest, converting it, or keeping the relationship?',
      validation:
        'The customer pressure is revealing uncertainty about which part of demand is genuinely dependable.',
      summary: [
        'Market activity and dependable demand have not yet been cleanly separated.',
        'The weakest point in attraction, conversion, or retention needs to become the primary evidence test.',
      ],
    },
  },
  {
    test: /\b(grow|growth|scale|scaling|expand|expansion|capital|funding|product|feature)\b/i,
    value: {
      id: 'growth',
      gravity: 'STEADY',
      reframe:
        'The growth ambition is running ahead of certainty about which part of the current model will carry the extra weight.',
      hold:
        'Expansion becomes safer when the assumption doing most of the work is made visible.',
      invitation:
        'What is the growth plan relying on most heavily: people, cash, or unproven demand?',
      validation:
        'The opportunity is real, but so is the concentration of risk underneath it.',
      summary: [
        'Growth is currently leaning on an assumption that has not been isolated and tested.',
        'Capacity, capital, and demand need clearer evidence before they can safely reinforce one another.',
      ],
    },
  },
];

const DEFAULT_REALITY: MentorReality = {
  id: 'operational_uncertainty',
  gravity: 'STEADY',
  reframe:
    'Something in the way this sits is still muddy.',
  hold:
    'No need to dress it up. Name the bit that is actually sticking.',
  invitation:
    'Is the sticky bit the decision, the money, or the energy to move it?',
  validation:
    'This is worth slowing down for, without turning it into a strategy speech.',
  summary: [
    'The surface problem and the real constraint are still mixed together.',
    'One honest test would beat another hour of turning it over.',
  ],
};

export function classifyMentorReality(content = ''): MentorReality {
  const text = content.trim();
  if (!text || /^focus area:/i.test(text)) return DEFAULT_REALITY;
  return REALITIES.find((entry) => entry.test.test(text))?.value ?? DEFAULT_REALITY;
}

function normaliseWords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

export function containsVerbatimParroting(
  input: string,
  response: string,
  sequenceLength = 6,
): boolean {
  const source = normaliseWords(input);
  const output = ` ${normaliseWords(response).join(' ')} `;
  if (source.length < sequenceLength) return false;

  for (let index = 0; index <= source.length - sequenceLength; index += 1) {
    const phrase = ` ${source.slice(index, index + sequenceLength).join(' ')} `;
    if (output.includes(phrase)) return true;
  }
  return false;
}

export function enforceAntiParroting(
  input: string,
  response: string,
  fallback: string,
): string {
  return containsVerbatimParroting(input, response) ? fallback : response;
}
